import { useOccasions } from '@/hooks/useProducts';
import { useCityNavigation } from '@/hooks/useCityNavigation';
import { useLocation } from 'react-router-dom';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';
import { egyptianLocations } from '@/data/locations';
import { optimizeImageUrl } from '@/utils/image-optimizer';
import { useEffect, useState } from 'react';

interface FlowerAuraCategoriesProps {
  language: string;
}

const IMAGE_SIZE = 164;
const HERO_BANNER_SRC = '/floweraura-assets/premium-flower-landingpage-desktop.webp';

const FlowerAuraCategories = ({ language }: FlowerAuraCategoriesProps) => {
  const { navigateWithCity, buildCityPath } = useCityNavigation();
  const { data: occasions, isLoading } = useOccasions();
  const location = useLocation();
  const { selectedCity } = useDeliveryLocation();
  const [visibleCount, setVisibleCount] = useState(6);

  // City home: /en-eg/alexandria (optional trailing slash); ignore unknown segments like "area"
  const normalizedPath =
    location.pathname.replace(/\/+$/, '') === '' ? '/' : location.pathname.replace(/\/+$/, '');
  const homeCityMatch = normalizedPath.match(/^\/(?:en-eg|ar-eg)\/([^/]+)$/);
  const segment = homeCityMatch?.[1];
  const cityFromPath = segment ? egyptianLocations.find((c) => c.slug === segment) : undefined;
  const city = cityFromPath ?? (!segment ? selectedCity : null);

  const cityNameEn = city?.name_en || city?.name;
  const cityNameAr = city?.name_ar;

  useEffect(() => {
    const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;
    const initialCount = isMobileViewport ? 4 : 6;
    setVisibleCount(initialCount);

    const showAll = () => setVisibleCount(6);
    if (isMobileViewport) {
      const winWithIdle = window as Window & {
        requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
        cancelIdleCallback?: (handle: number) => void;
      };

      if (typeof winWithIdle.requestIdleCallback === 'function') {
        const idleId = winWithIdle.requestIdleCallback(showAll, { timeout: 1600 });
        return () => winWithIdle.cancelIdleCallback?.(idleId);
      }

      const timeoutId = window.setTimeout(showAll, 1000);
      return () => window.clearTimeout(timeoutId);
    }
  }, []);

  // Progressive render on mobile to reduce initial image pressure.
  const displayOccasions = occasions?.slice(0, visibleCount) || [];

  const titleEn =
    cityNameEn != null && cityNameEn !== ''
      ? `Online Flower Delivery In ${cityNameEn}`
      : 'Online Flower Delivery In Egypt';
  const titleAr =
    cityNameAr != null && cityNameAr !== ''
      ? `شراء ورد أونلاين في ${cityNameAr}`
      : 'شراء ورد أونلاين في مصر';

  const heroTitle = language === 'ar' ? titleAr : titleEn;

  // City intro paragraph moved to CityIntroParagraph component, rendered
  // below the "Flower Guides" section in Index.tsx — see commit history.

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="relative mb-8">
          <img 
            src={optimizeImageUrl(HERO_BANNER_SRC, 700)}
            alt="Premium Flower Landing Page"
            className="w-full h-auto rounded-lg shadow-lg premium-rtl-flip"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            width="380"
            height="102"
            sizes="(max-width: 768px) 100vw, 665px"
            style={{ aspectRatio: '380/102' }}
            onError={(e) => {
              const img = e.currentTarget;
              if (img.dataset.fallbackApplied === '1') return;
              img.dataset.fallbackApplied = '1';
              img.src = HERO_BANNER_SRC;
            }}
          />
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-2">{heroTitle}</h1>
        </div>

        {/* H2 section header for occasions — gives Google a clear taxonomy label. */}
        <h2 className="text-xl md:text-2xl font-semibold text-foreground text-center mb-6">
          {language === 'ar' ? 'الورد حسب المناسبة' : 'Gift by Occasion'}
        </h2>
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 justify-center">
          {isLoading ? (
            // Show skeleton loaders with exact dimensions
            [...Array(6)].map((_, index) => (
              <li key={index} className="fadeIn addBtMrgn flex flex-col items-center">
                <div className="animate-pulse rounded-2xl bg-muted mb-2" style={{ width: IMAGE_SIZE, height: IMAGE_SIZE }}></div>
                <div className="h-4 bg-muted rounded w-20 mx-auto"></div>
              </li>
            ))
          ) : (
            // Show actual content with exact dimensions
            displayOccasions.map((occasion) => (
              <li key={occasion.id} className="fadeIn addBtMrgn flex flex-col items-center">
                <a 
                  href={buildCityPath(`/occasion/${occasion.slug}`)}
                  onClick={(e) => {
                    e.preventDefault();
                    navigateWithCity(`/occasion/${occasion.slug}`);
                  }}
                  className="block group"
                  style={{ width: IMAGE_SIZE }}
                >
                  <span className="block" style={{ width: IMAGE_SIZE, height: IMAGE_SIZE }}>
                    <img
                      src={optimizeImageUrl(occasion.image_url || "/floweraura-assets/luxury-flower-2.webp", IMAGE_SIZE * 2)}
                      loading="lazy"
                      alt=""
                      aria-hidden="true"
                      srcSet={`
                        ${optimizeImageUrl(occasion.image_url || "/floweraura-assets/luxury-flower-2.webp", IMAGE_SIZE)} 1x,
                        ${optimizeImageUrl(occasion.image_url || "/floweraura-assets/luxury-flower-2.webp", IMAGE_SIZE * 2)} 2x
                      `}
                      sizes={`${IMAGE_SIZE}px`}
                      title={language === 'ar' ? occasion.name_ar : occasion.name_en}
                      width={IMAGE_SIZE}
                      height={IMAGE_SIZE}
                      style={{ 
                        display: 'inline', 
                        width: IMAGE_SIZE, 
                        height: IMAGE_SIZE,
                        aspectRatio: '1/1'
                      }}
                      className="object-cover rounded-2xl group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img.dataset.fallbackApplied === '1') return;
                        img.dataset.fallbackApplied = '1';
                        const fallbackSrc = occasion.image_url || "/floweraura-assets/luxury-flower-2.webp";
                        img.src = fallbackSrc;
                        img.srcset = '';
                      }}
                    />
                  </span>
                  <span className="catgorySpanName block text-center mt-2 text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                    {language === 'ar' ? occasion.name_ar : occasion.name_en}
                  </span>
                </a>
              </li>
            ))
          )}
        </ul>
      </div>
    </section>
  );
};

export default FlowerAuraCategories;